import { runFileRecordVersionHistoryWorkflow } from "@/action-dialog";
import {
  isCanvasIntrinsicRecord,
  ObjectActionPaneHeaderActions,
  ObjectActionPaneHeaderKebabMenu,
  useResolvedFileRecordActions,
  type ObjectActionPaneHeaderRenderer,
} from "@/object-actions";
import type {
  ObjectActionHandlers,
  ObjectActionId,
} from "@/object-actions/types";
import { useFileRecordHistoryTrigger } from "@/panes/file-record-detail/FileRecordHistoryTriggerContext";
import { resolveFileRecordShowPublish } from "@/panes/file-record-detail/publish";
import { useFileRecordPaneHeaderKebabHandlerDeps } from "@/panes/file-record-detail/useFileRecordPaneHeaderKebabHandlerDeps";
import type { FileRecord } from "@cohesive/api-client";
import { useMemo, useState } from "react";
import { FilePublishButton } from "./FilePublishButton";

export type FileRecordPaneHeaderActionsProps = {
  record: FileRecord;
  canEdit: boolean;
  canManage: boolean;
  isNotionMarkdown: boolean;
  apiVersioningEnabled: boolean;
};

function fileRecordDisplayName(record: FileRecord): string {
  return record.path?.split("/").filter(Boolean).pop() || "File";
}

export function FileRecordPaneHeaderActions({
  record,
  canEdit,
  canManage,
  isNotionMarkdown,
  apiVersioningEnabled,
}: FileRecordPaneHeaderActionsProps) {
  const [kebabOpen, setKebabOpen] = useState(false);
  const displayName = fileRecordDisplayName(record);
  const kebabHandlerDeps = useFileRecordPaneHeaderKebabHandlerDeps(record);
  const historyTrigger = useFileRecordHistoryTrigger();

  // Notion-like files own their history in the tiptap snapshot context (bridged
  // out via the trigger); everything else uses the API versioning dialog.
  const notionHistoryReady =
    isNotionMarkdown && canEdit && !!historyTrigger?.isRegistered;
  const showVersionHistory = apiVersioningEnabled || notionHistoryReady;

  const state = {
    canShare: canEdit,
    canEdit,
    canDelete: canManage,
    canMove: canManage,
    showPublish: resolveFileRecordShowPublish({ record, canEdit }),
    showVersionHistory,
    // Blocks open here like documents and keep the file verbs; the flag
    // only ever adds Expand, which needs a canvas node the pane has not got.
    isCanvasIntrinsic: isCanvasIntrinsicRecord({ record }),
  };

  const kebabExtraHandlers = useMemo((): ObjectActionHandlers => {
    if (isNotionMarkdown) {
      return { "version-history": () => historyTrigger?.openHistory() };
    }
    return {
      "version-history": () =>
        window.setTimeout(
          () => void runFileRecordVersionHistoryWorkflow({ record }),
          0,
        ),
    };
  }, [historyTrigger, isNotionMarkdown, record]);

  const { actions, kind } = useResolvedFileRecordActions({
    record,
    surface: "pane-header",
    state,
    handlerDeps: kebabHandlerDeps,
  });

  const { actions: kebabActions } = useResolvedFileRecordActions({
    record,
    surface: "left-rail",
    enabled: kebabOpen,
    state,
    handlerDeps: kebabHandlerDeps,
    extraHandlers: kebabExtraHandlers,
  });

  // The pane already shows this record — "Open" is redundant in its kebab.
  const kebabMenuActions = useMemo(
    () => kebabActions.filter((action) => action.id !== "open"),
    [kebabActions],
  );

  const customRenderers = useMemo((): Partial<
    Record<ObjectActionId, ObjectActionPaneHeaderRenderer>
  > => {
    const renderers: Partial<
      Record<ObjectActionId, ObjectActionPaneHeaderRenderer>
    > = {};

    if (record.id) {
      renderers.publish = () => <FilePublishButton record={record} />;
    }

    return renderers;
  }, [record]);

  if (!kind) {
    return null;
  }

  return (
    <ObjectActionPaneHeaderActions
      actions={actions}
      kind={kind}
      name={displayName}
      customRenderers={customRenderers}
      kebabMenu={
        <ObjectActionPaneHeaderKebabMenu
          actions={kebabMenuActions}
          kind={kind}
          name={displayName}
          open={kebabOpen}
          onOpenChange={setKebabOpen}
        />
      }
    />
  );
}
